// The in-chat join card's choreography — the genome reel's beats, resequenced for
// the "an agent joined your team" moment.
//
// Every duration is `lib/genome-timing`'s, i.e. native's `ConstellationTiming`:
// the card is the SAME figure assembling at the SAME speed as the Your Agents
// hero. What differs is the ORDER, and it differs the same way on all three
// surfaces (iOS/Android `AgentJoinCard`): the team that was already there lands
// first, one by one, and only once it has settled do the new hire(s) get their
// own beat — so "here's your team, and here's who just joined" is a sequence you
// can watch, not a ring that was silently already full.
//
// Two more divergences from the hero, both of which the native card also has:
// once everything has landed the agent ring does NOT stop — it keeps orbiting the
// centre for as long as the card is on screen, one revolution every
// `ORBIT_ONCE_MS` (native's `orbitOnceSec`) — and the agents follow the structure
// reveal immediately, with no `NAMES_HIDE_GAP_MS` breather. That gap covers the
// hero's skill-NAME beat; this card never plays one, so borrowing it would put the
// whole reel 90ms behind both phones.

import {
  AGENT_STAGGER_MS,
  MAESTRO_GAP_MS,
  MAESTRO_SETTLE_MS,
  REEL_START_MS,
  STRUCTURE_REVEAL_MS,
} from './genome-timing';

/** One revolution of the settled agent ring (native `ConstellationTiming.orbitOnceSec` = 6.5s). */
export const ORBIT_ONCE_MS = 6500;

/**
 * Lead-in before a hire that folded into an ALREADY-MOUNTED run flies out. Its
 * beat is measured from the moment it arrives, not from the reel start — the
 * rest of the figure is already settled and must not move.
 */
export const TOP_UP_LEAD_MS = 60;

export interface JoinReelTimeline {
  /** When the first already-on-the-team node starts landing. */
  existingAtMs: number;
  /** When the first NEWLY joined node starts landing — its own beat, after the team has settled. */
  newAtMs: number;
  /** When the settled ring starts its continuous orbit. */
  orbitAtMs: number;
}

/**
 * The card's beat boundaries for a given split of settled team vs. new hires.
 *
 * Every number is native's, and the sequence is native's card verbatim — including that
 * the agents follow the structure reveal with NO breather (the hero's `namesHideGapMs`
 * covers a names beat this card doesn't play) and that the new-hire beat is measured from
 * the moment it opens, so the `TOP_UP_LEAD_MS` lead-in shifts the nodes but not the orbit.
 */
export function joinReelTimeline(existingCount: number, newCount: number): JoinReelTimeline {
  const existingAtMs = REEL_START_MS + STRUCTURE_REVEAL_MS;
  const existingSpanMs = Math.max(existingCount - 1, 0) * AGENT_STAGGER_MS;
  // With no prior team there is no first beat to wait on — the hires ARE the team's arrival.
  const newBeatAtMs = existingCount > 0 ? existingAtMs + existingSpanMs + MAESTRO_GAP_MS : existingAtMs;
  const newAtMs = existingCount > 0 ? newBeatAtMs + TOP_UP_LEAD_MS : newBeatAtMs;
  const newSpanMs = Math.max(newCount - 1, 0) * AGENT_STAGGER_MS;
  return { existingAtMs, newAtMs, orbitAtMs: newBeatAtMs + newSpanMs + MAESTRO_GAP_MS + MAESTRO_SETTLE_MS };
}

/** When node `index` of `[...existing, ...joining]` starts landing, measured from the reel start. */
export function joinNodeDelayMs(index: number, existingCount: number, timeline: JoinReelTimeline): number {
  return index < existingCount
    ? timeline.existingAtMs + index * AGENT_STAGGER_MS
    : timeline.newAtMs + (index - existingCount) * AGENT_STAGGER_MS;
}
